"use client";

import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocale, formatNumber } from "@/i18n/format";

export interface KpiMetric {
  current: string | number;
  previous: string | number;
  deltaPct: number | null;
}

export interface KpiStripProps {
  kpi: {
    posts: KpiMetric;
    impressions: KpiMetric;
    engagement: KpiMetric;
    followers: KpiMetric;
  };
  /** Watch-time roll-up for video posts; the tile shows only when there is one. */
  video?: { avgPctWatched: number | null; videoPosts: number } | null;
}

function DeltaPill({ deltaPct }: { deltaPct: number | null }) {
  if (deltaPct === null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" /> —
      </span>
    );
  }
  const positive = deltaPct > 0;
  const negative = deltaPct < 0;
  const cls = positive
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
    : negative
      ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400"
      : "bg-muted text-muted-foreground";
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const sign = positive ? "▲" : negative ? "▼" : "";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      <Icon className="h-3 w-3" />
      {sign} {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
}

function Tile({ label, metric }: { label: string; metric: KpiMetric }) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  const n = typeof metric.current === "string" ? Number(metric.current) : metric.current;
  const formatted = Number.isFinite(n) ? formatNumber(n, locale) : String(metric.current);
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold">
        {formatted}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <DeltaPill deltaPct={metric.deltaPct} />
        <span className="text-xs text-muted-foreground">{t("kpi.vsPreviousPeriod")}</span>
      </div>
    </div>
  );
}

function VideoTile({ avgPctWatched, videoPosts }: { avgPctWatched: number; videoPosts: number }) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  return (
    <div className="rounded-lg border bg-card p-4" data-testid="kpi-avg-pct-watched">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("kpi.avgPctWatched")}
      </div>
      <div className="mt-2 text-2xl font-semibold">
        {formatNumber(avgPctWatched, locale, { maximumFractionDigits: 1 })}%
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        {t("kpi.videoPosts", { count: videoPosts })}
      </div>
    </div>
  );
}

export function KpiStrip({ kpi, video }: KpiStripProps) {
  const { t } = useTranslation("analytics");
  const showVideo = video?.avgPctWatched != null && video.videoPosts > 0;
  return (
    <div
      className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${showVideo ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}
    >
      <Tile label={t("kpi.posts")} metric={kpi.posts} />
      <Tile label={t("kpi.impressions")} metric={kpi.impressions} />
      <Tile label={t("kpi.engagement")} metric={kpi.engagement} />
      <Tile label={t("kpi.followers")} metric={kpi.followers} />
      {showVideo && (
        <VideoTile avgPctWatched={video!.avgPctWatched!} videoPosts={video!.videoPosts} />
      )}
    </div>
  );
}
